import cx from 'classnames';
import { ForgeList, ForgeListItem } from '@tylertech/forge-react';
import React, { PropsWithChildren, ReactNode, RefObject, useState, useCallback } from 'react';
import { none, Option, some } from 'ts-option';
import {
  ConnectDragPreview,
  ConnectDragSource,
  ConnectDropTarget,
  DragSource,
  DragSourceConnector,
  DropTarget,
  DropTargetConnector,
} from 'react-dnd';
import './index.scss';
import { ForgeCard } from '@tylertech/forge-react';
import _ from 'lodash';
import { FeatureFlags } from 'common/feature_flags';
import { DnDKitDragDrop } from './DnDKitDragDrop';
import { DragHandleIcon } from './DragHandleIcon';
interface DraggableProps {
  onDrag: () => void;
  isDropped: boolean;
}
interface DroppableProps {
  onHovering: (idx: number) => void;
  isHoveringTop: boolean;
  isHoveringBottom: boolean;
  onDrop: (idx: number) => void;
  beingDragged?: boolean;
}
interface BaseItemProps {
  idx: number;
  element: ReactNode;
  iconName?: string;
  itemRef: RefObject<ReactNode>;
  trailingElement?: ReactNode;
  className?: string;
  type: DragDropContainerType;
  useHoverColor?: boolean;
  additionalItemProps?: any;
  mainElement?: ReactNode;
  sourceComponent?: string | undefined;
}

type DraggableItemProps = DraggableProps & DroppableProps & BaseItemProps;
type DroppableItemProps = DroppableProps & BaseItemProps;

interface DragInjectedProps {
  connectDragSource: ConnectDragSource;
  connectDragPreview: ConnectDragPreview;
}
interface DropInjectedProps {
  connectDropTarget: ConnectDropTarget;
}
type DnDInjectedProps = DragInjectedProps & DropInjectedProps;

const dragActions = {
  beginDrag(props: DraggableItemProps, monitor: any): { idx: number } {
    props.onDrag();
    return { idx: props.idx };
  },
  canDrag(props: DraggableItemProps, monitor: any): boolean {
    return true;
  },
  endDrag(props: DraggableItemProps) {},
};

const dragCollect = (connectDrag: DragSourceConnector) => {
  return {
    connectDragSource: connectDrag.dragSource(),
    connectDragPreview: connectDrag.dragPreview(),
  };
};

const dropActions = {
  hover(props: DraggableItemProps, monitor: any) {
    props.onHovering(props.idx);
  },
  drop(props: DraggableItemProps) {
    props.onDrop(props.idx);
  },
};

const dropCollect = (connectDrop: DropTargetConnector) => {
  return {
    connectDropTarget: connectDrop.dropTarget(),
  };
};

const mainContainerForType = (type: DragDropContainerType) => {
  switch (type) {
    case DragDropContainerType.TABLE:
      return 'tbody';
    case DragDropContainerType.LIST:
      return 'ul';
    case DragDropContainerType.FORGE_LIST:
      return ForgeList;
    case DragDropContainerType.CONDITIONS:
    case DragDropContainerType.FORGE_CARD:
      return 'div';
  }
};

// if you want everything to be draggable and don't want to construct the interface
export const makeDefaultDragDropElementWrapper = (els: ReactNode[]): DragDropElementWrapper[] => {
  return els.map((el) => {
    return {
      dragAndDroppable: true,
      element: el,
    };
  });
};

// if you don't want everything to be draggable
export const makeCustomDragDropElementWrapper = (
  els: ReactNode[],
  isDraggableFunc: (el: ReactNode) => boolean,
): DragDropElementWrapper[] =>
  els.map((el) => ({
    dragAndDroppable: isDraggableFunc(el),
    element: el,
  }));

interface ElementProps {
  /** Pass additional props to the element container */
  additionalContainerProps?: any;
  /** Classes for the element container */
  className?: string;
  /** Content for the element */
  element: ReactNode;
  /** Ref for the element, stored in the container state */
  itemRef: RefObject<ReactNode>;
  /** Content for element that will go in 'trailing' slot rather than 'title' slot on a ForgeList only */
  trailingElement?: ReactNode;
  dragContainerClassName: string;
  idx: number;
  /** If undefined, then the element will not be connected */
  connectDropTarget?: ConnectDropTarget;
  /** If undefined, then the element will not be connected */
  connectDragSource?: ConnectDragSource;
  /** If undefined, then the element will not be connected */
  connectDragPreview?: ConnectDragPreview;
  /** When not undefined, element will be the condition's title and mainElement will be the content */
  mainElement?: ReactNode;
  beingDragged?: boolean; // currently only used by forge-card,
  sourceComponent?: string | undefined;
  iconName?: string; // Icon name for drag handle
}
const ForgeListElement: React.FC<ElementProps> = ({
  additionalContainerProps = {},
  className,
  element,
  trailingElement,
  dragContainerClassName,
  idx,
  iconName = 'drag_handle',
  itemRef,
  connectDragPreview = (node) => node,
  connectDragSource = (node) => node,
  connectDropTarget = (node) => node,
  sourceComponent,
}) => {
  const id = sourceComponent ? `${sourceComponent}-drag-element-${idx}` : `drag-element-${idx}`;
  return (
    <ForgeListItem
      {...additionalContainerProps}
      data-testId={additionalContainerProps.dataTestId}
      id={id}
      className={cx('drag-element', className)}
      ref={itemRef}
    >
      <button>
        {connectDropTarget(
          connectDragPreview(
            <div className="drag-element-forge-list-content">
              {connectDragSource(
                <div className={cx('drag-handle', dragContainerClassName)}>
                  <DragHandleIcon name={iconName} />
                </div>,
              )}
              <span>{element}</span>
              {trailingElement && <span className="trailing-content">{trailingElement}</span>}
            </div>,
          ),
        )}
      </button>
    </ForgeListItem>
  );
};

const ForgeCardElement: React.FC<ElementProps> = ({
  additionalContainerProps = {},
  className,
  element,
  dragContainerClassName,
  idx,
  iconName = 'drag_horizontal',
  itemRef,
  connectDragPreview = (node) => node,
  connectDragSource = (node) => node,
  connectDropTarget = (node) => node,
  beingDragged,
}) => {
  return (
    <div>
      {connectDropTarget(
        connectDragPreview(
          <div className={`forge-card ${beingDragged ? 'dragging' : ''}`}>
            <ForgeCard
              {...additionalContainerProps}
              data-testId={additionalContainerProps.dataTestId}
              id={`drag-element-${idx}`}
              className={cx('drag-element', className)}
              ref={itemRef}
              outlined
            >
              <div className="forge-card">
                {connectDragSource(
                  <div className={cx('forge-card-drag-handle', dragContainerClassName)}>
                    <DragHandleIcon name={iconName} />
                  </div>,
                )}
                {element}
              </div>
            </ForgeCard>
          </div>,
        ),
      )}
    </div>
  );
};

const ListElement: React.FC<ElementProps> = ({
  additionalContainerProps = {},
  className,
  element,
  dragContainerClassName,
  iconName = 'drag_indicator',
  itemRef,
  connectDragPreview = (node) => node,
  connectDragSource = (node) => node,
  connectDropTarget = (node) => node,
}) => {
  const icon = <DragHandleIcon name={iconName} />;

  return (
    <>
      {connectDropTarget(
        connectDragPreview(
          <li {...additionalContainerProps} className={cx('drag-element', className)} itemRef={itemRef}>
            {connectDragSource(<div className={cx('drag-handle', dragContainerClassName)}>{icon}</div>)}
            {element}
          </li>,
        ),
      )}
    </>
  );
};

const TableElement: React.FC<ElementProps> = ({
  additionalContainerProps = {},
  className,
  element,
  dragContainerClassName,
  iconName = 'drag_indicator',
  itemRef,
  connectDragPreview = (node) => node,
  connectDragSource = (node) => node,
  connectDropTarget = (node) => node,
}) => {
  return (
    <>
      {connectDropTarget(
        connectDragPreview(
          <tr {...additionalContainerProps} className={cx('drag-element', className)} itemRef={itemRef}>
            {connectDragSource(
              <td className={cx('drag-handle', dragContainerClassName)}>
                <DragHandleIcon name={iconName} />
              </td>,
            )}
            {element}
          </tr>,
        ),
      )}
    </>
  );
};

const ConditionElement: React.FC<ElementProps> = ({
  additionalContainerProps = {},
  className,
  element,
  dragContainerClassName,
  iconName = 'drag_handle',
  itemRef,
  connectDragPreview = (node) => node,
  connectDragSource = (node) => node,
  connectDropTarget = (node) => node,
  mainElement,
}) => {
  return (
    <>
      {connectDropTarget(
        connectDragPreview(
          <div
            {...additionalContainerProps}
            className={cx('drag-element', 'conditional-config', className)}
            itemRef={itemRef}
          >
            <div>
              {connectDragSource(
                <div className={cx('drag-handle', 'conditional-config-header', dragContainerClassName)}>
                  <DragHandleIcon name={iconName} />
                  {element}
                </div>,
              )}
            </div>
            <div>{mainElement}</div>
          </div>,
        ),
      )}
    </>
  );
};

const UnDraggableElement: React.FC<BaseItemProps> = ({
  className,
  type,
  element,
  iconName,
  trailingElement,
  idx,
  itemRef,
}) => {
  const props = {
    element,
    trailingElement,
    className,
    dragContainerClassName: 'drag-disabled',
    idx,
    iconName,
    itemRef,
  };

  switch (type) {
    case DragDropContainerType.FORGE_LIST:
      return <ForgeListElement {...props} additionalContainerProps={{ disabled: true, selected: false }} />;
    case DragDropContainerType.LIST:
      return <ListElement {...props} />;
    case DragDropContainerType.TABLE:
      return <TableElement {...props} />;
    case DragDropContainerType.CONDITIONS:
      return <ConditionElement {...props} />;
    case DragDropContainerType.FORGE_CARD:
      return <ForgeCardElement {...props} />;
  }
};

class DraggableElement extends React.Component<DraggableItemProps & DnDInjectedProps> {
  render() {
    const {
      element,
      trailingElement,
      mainElement,
      type,
      iconName,
      className,
      connectDropTarget,
      connectDragSource,
      connectDragPreview,
      isHoveringTop,
      isHoveringBottom,
      isDropped,
      useHoverColor,
      additionalItemProps = {},
      idx,
      itemRef,
      beingDragged,
      sourceComponent,
    } = this.props;

    const isHovering = isHoveringTop || isHoveringBottom;

    const props = {
      element,
      trailingElement,
      mainElement,
      iconName,
      className: cx('drop-enabled', className, {
        hovering: isHovering && useHoverColor,
        dropped: isDropped,
        'hover-top': isHoveringTop,
        'hover-bottom': isHoveringBottom,
      }),
      dragContainerClassName: 'drag-enabled',
      connectDragSource,
      connectDragPreview,
      connectDropTarget,
      idx,
      itemRef,
      sourceComponent,
    };

    switch (type) {
      case DragDropContainerType.FORGE_LIST:
        return <ForgeListElement {...props} additionalContainerProps={additionalItemProps} />;
      case DragDropContainerType.LIST:
        return <ListElement {...props} />;
      case DragDropContainerType.TABLE:
        return <TableElement {...props} />;
      case DragDropContainerType.CONDITIONS:
        return <ConditionElement {...props} />;
      case DragDropContainerType.FORGE_CARD:
        return <ForgeCardElement {...props} beingDragged={beingDragged} />;
    }
  }
}
class DroppableElement extends React.Component<DroppableItemProps & DropInjectedProps> {
  render() {
    const {
      className,
      type,
      element,
      iconName,
      trailingElement,
      mainElement,
      connectDropTarget,
      isHoveringTop,
      isHoveringBottom,
      useHoverColor,
      idx,
      itemRef,
    } = this.props;
    const isHovering = useHoverColor && (isHoveringTop || isHoveringBottom);
    const props = {
      element,
      idx,
      iconName,
      trailingElement,
      mainElement,
      className: cx('drop-enabled', className, {
        hovering: isHovering,
        'hover-top': isHoveringTop,
        'hover-bottom': isHoveringBottom,
      }),
      dragContainerClassName: 'drag-disabled',
      connectDropTarget,
      itemRef,
    };

    switch (type) {
      case DragDropContainerType.FORGE_LIST:
        return <ForgeListElement {...props} />;
      case DragDropContainerType.LIST:
        return <ListElement {...props} />;
      case DragDropContainerType.TABLE:
        return <TableElement {...props} />;
      case DragDropContainerType.CONDITIONS:
        return <ConditionElement {...props} />;
      case DragDropContainerType.FORGE_CARD:
        return <ForgeCardElement {...props} />;
    }
  }
}

const DRAG_ITEM_TYPE = 'DRAG_DROP_LIST';

const ConnectedDraggableElement = DropTarget(
  DRAG_ITEM_TYPE,
  dropActions,
  dropCollect,
)(
  // eslint-disable-line
  DragSource(DRAG_ITEM_TYPE, dragActions, dragCollect)(DraggableElement), // eslint-disable-line
);
const ConnectedDroppableElement = DropTarget(DRAG_ITEM_TYPE, dropActions, dropCollect)(DroppableElement);

export interface DragDropElementWrapper<T = unknown> {
  className?: string;
  id?: string;
  dragAndDroppable: boolean;
  droppable?: boolean;
  element: ReactNode; // Main content or title element for CONDITIONS type
  trailingElement?: ReactNode; // Trailing content for lists
  additionalItemProps?: Record<string, unknown>; // Additional props to pass to the container
  mainElement?: ReactNode; // Main content for CONDITIONS type
  value?: T; // Data associated with the item
  iconName?: string; // Icon name for drag handle
}

export enum DragDropContainerType {
  LIST = 'LIST',
  TABLE = 'TABLE',
  FORGE_LIST = 'FORGE_LIST',
  CONDITIONS = 'CONDITIONS',
  FORGE_CARD = 'FORGE_CARD',
}

export interface DragDropContainerProps<T> {
  className?: string;
  type: DragDropContainerType;
  iconName?: string; // Icon name for drag handle
  items: T[];
  childElements: DragDropElementWrapper[];
  tableHeadContent?: ReactNode; // required if type is TABLE
  onDrop: (newItems: T[]) => void;
  dense?: boolean;
  useHoverColor?: boolean; // defaults to true
  sourceComponent?: string | undefined;
  usePortal?: boolean; // For dnd-kit: whether to render DragOverlay in a portal
}

interface PositionChange<T> {
  startPosn: number;
  endPosn: number;
  items: T[];
}

interface DragDropContainerState<T> {
  dragging: Option<PositionChange<T>>;
  dropped: Option<number>;
  childRefs: RefObject<ReactNode>[];
}

class DragDropContainer<T> extends React.Component<
  PropsWithChildren<DragDropContainerProps<T>>,
  DragDropContainerState<T>
> {
  state: DragDropContainerState<T> = {
    dragging: none,
    dropped: none,
    childRefs: this.props.childElements.map((_c) => React.createRef<ReactNode>()),
  };

  static defaultProps = {
    useHoverColor: true,
  };

  onDrag = (idx: number) => {
    const draggingRef = this.state.childRefs[idx]?.current as any;
    // this deactivates the 'pressed' state on the forge list item, which otherwise stays on the index position we dragged from
    if (draggingRef?.deactivateRipple) draggingRef.deactivateRipple();

    // we store the items in the dragging state because if the component
    // re-renders or something, we don't want to be firing drop events using
    // indices on a list which has changed out from under us
    this.setState({ dragging: some({ startPosn: idx, endPosn: idx, items: this.props.items }) });
  };

  onHovering = (idx: number) => {
    this.state.dragging.forEach((drag) => {
      this.setState({ dragging: some({ ...drag, endPosn: idx }) });
    });
  };

  // There were occurrences where ripples (highlight) still persisted after an item was dropped.
  // So far, this has only been observed in the Custom Sort Modal, identified by adding a sourceComponent.
  removeTrailsOfRippleEffect = (drag: PositionChange<T>) => {
    const replacedElementId = `#${this.props.sourceComponent}-drag-element-${drag.startPosn}`;
    const replacedItem: HTMLElement | null = document.querySelector(replacedElementId);
    replacedItem?.shadowRoot
      ?.querySelector('.forge-list-item')
      ?.classList.remove('mdc-ripple-upgraded--foreground-activation');
    replacedItem?.shadowRoot?.querySelector('.forge-list-item')?.removeAttribute('style');
  };

  onDrop = () => {
    this.state.dragging.forEach((drag) => {
      const item = this.props.items[drag.startPosn];
      const others = this.props.items.filter((_item, idx) => idx !== drag.startPosn);
      const endPosn = Math.max(0, drag.endPosn);

      const left = others.slice(0, endPosn);
      const right = others.slice(endPosn);

      const changes = [...left, item, ...right];

      this.props.onDrop(changes);
      const isForCustomSort = this.props.sourceComponent && this.props.sourceComponent == 'custom-sort';
      const elementId = isForCustomSort
        ? `#${this.props.sourceComponent}-drag-element-${endPosn}`
        : `#drag-element-${endPosn}`;
      const droppedItem: HTMLElement | null = document.querySelector(elementId);

      if (droppedItem) droppedItem.focus();
      this.setState({ dragging: none, dropped: some(endPosn) });

      if (isForCustomSort) {
        this.removeTrailsOfRippleEffect(drag);
      }

      // clear the just dropped indicator
      setTimeout(() => {
        this.setState({ dropped: none });
      }, 1000);
    });
  };

  render() {
    const contents = this.props.childElements.map((child: DragDropElementWrapper, idx: number) => {
      const key = child.id || idx.toString();
      let isDragged;
      if (this.state.dragging.isDefined) {
        isDragged = idx === this.state.dragging.get.startPosn;
      }
      if (child.dragAndDroppable) {
        return (
          <ConnectedDraggableElement
            element={child.element}
            itemRef={this.state.childRefs[idx]}
            trailingElement={child.trailingElement}
            type={this.props.type}
            iconName={this.props.iconName}
            className={child.className}
            idx={idx}
            key={key}
            onHovering={this.onHovering}
            isDropped={this.state.dropped.map((d) => d === idx).getOrElseValue(false)}
            isHoveringTop={this.state.dragging
              .map(({ startPosn, endPosn }) => startPosn > endPosn && endPosn === idx)
              .getOrElseValue(false)}
            isHoveringBottom={this.state.dragging
              .map(({ startPosn, endPosn }) => startPosn < endPosn && endPosn === idx)
              .getOrElseValue(false)}
            onDrop={this.onDrop}
            onDrag={() => this.onDrag(idx)}
            useHoverColor={this.props.useHoverColor}
            additionalItemProps={child.additionalItemProps}
            mainElement={child.mainElement}
            beingDragged={isDragged}
            sourceComponent={this.props.sourceComponent}
          />
        );
      } else if (child.droppable) {
        return (
          <ConnectedDroppableElement
            element={child.element}
            itemRef={this.state.childRefs[idx]}
            iconName={this.props.iconName}
            trailingElement={child.trailingElement}
            mainElement={child.mainElement}
            type={this.props.type}
            className={child.className}
            idx={idx}
            key={key}
            onHovering={this.onHovering}
            isHoveringTop={this.state.dragging
              .map(({ startPosn, endPosn }) => startPosn > endPosn && endPosn === idx)
              .getOrElseValue(false)}
            isHoveringBottom={this.state.dragging
              .map(({ startPosn, endPosn }) => startPosn < endPosn && endPosn === idx)
              .getOrElseValue(false)}
            onDrop={this.onDrop}
            useHoverColor={this.props.useHoverColor}
            beingDragged={isDragged}
          />
        );
      } else {
        return (
          <UnDraggableElement
            className={child.className}
            iconName={this.props.iconName}
            itemRef={this.state.childRefs[idx]}
            element={child.element}
            trailingElement={child.trailingElement}
            mainElement={child.mainElement}
            idx={idx}
            type={this.props.type}
            key={key}
          />
        );
      }
    });
    const Container = mainContainerForType(this.props.type);
    const dense = this.props.dense === undefined ? true : this.props.dense;
    const props = {
      className: cx('dnd-container', this.props.className, {
        active: this.state.dragging !== none,
        'forge-dnd-container': this.props.type === DragDropContainerType.FORGE_LIST,
      }),
      dense: this.props.type === DragDropContainerType.FORGE_LIST ? dense : undefined,
    };

    return <Container {...props}>{contents}</Container>;
  }
}

// Enhanced wrapper component that handles both react-dnd and dnd-kit implementations
export const UnifiedDragDropContainer = <T = unknown,>(
  props: PropsWithChildren<DragDropContainerProps<T>>,
) => {
  const useDndKit = FeatureFlags.valueOrDefault('use_dnd_kit', false);

  if (useDndKit) {
    // Convert props to dnd-kit format
    const { items, onDrop, childElements, type, className, dense, tableHeadContent } = props;

    // Handle conversion for dnd-kit
    const handleDndKitReorder = (newItems: DragDropElementWrapper<T>[]) => {
      // Extract the original data from the dnd-kit items
      const reorderedData = newItems.map((item) => item.value || item) as T[];
      onDrop(reorderedData);
    };

    // Use childElements directly since they're already in DragDropElementWrapper format
    let dndKitItems: DragDropElementWrapper<T>[] = [];

    if (childElements && Array.isArray(childElements)) {
      // childElements are already in the correct format, just ensure they have proper values
      dndKitItems = childElements.map((child, index) => {
        const item = items?.[index];
        return {
          ...child,
          id: child.id || `item-${index}`,
          value: child.value || item,
        } as DragDropElementWrapper<T>;
      });
    } else if (items) {
      // If no childElements provided, create basic items
      dndKitItems = items.map((item, index) => ({
        id: `item-${index}`,
        element: item as any,
        dragAndDroppable: true,
        value: item,
      }));
    }

    return (
      <DnDKitDragDrop
        items={dndKitItems}
        tableHeadContent={tableHeadContent}
        onReorder={handleDndKitReorder}
        type={type}
        className={className}
        iconName={props.iconName}
        dense={dense}
        usePortal={props.usePortal}
      />
    );
  } else {
    if (props.type === DragDropContainerType.TABLE && props.tableHeadContent) {
      return (
        <table>
          {props.tableHeadContent}
          <DragDropContainer {...props} />
        </table>
      );
    } else {
      // Use legacy react-dnd implementation
      return <DragDropContainer {...props} />;
    }
  }
};

/**
 * UnifiedDragDropContainer - A centralized drag-drop component that automatically switches
 * between react-dnd (legacy) and dnd-kit (modern) based on feature flag.
 *
 * Usage:
 * ```tsx
 * import DragDropContainer from 'common/components/DragDropContainer';
 *
 * const items = ['foo', 'bar', 'baz'];
 * const listItems = items.map(item => <span>{item}</span>);
 * const wrappedItems = makeDefaultDragDropElementWrapper(listItems);
 *
 * <DragDropContainer
 *   items={items}
 *   childElements={wrappedItems}
 *   onDrop={(newItems) => console.log(newItems)}
 *   type={DragDropContainerType.LIST}
 * />
 * ```
 *
 * No need to check feature flags or use different components - it's all handled internally!
 *
 * For react-dnd: Parent component needs DragDropContext(HTML5Backend) wrapper
 * For dnd-kit: No special wrapper needed (handled internally)
 */
export default UnifiedDragDropContainer;
